import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { useAuth } from "@/contexts/auth-context"
import { getLanguageFromToken } from "@/lib/jwt-utils"

export type Language = "en" | "fr"

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
}

const LanguageContext = createContext<LanguageContextType | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  const [language, setLanguage] = useState<Language>("en")

  useEffect(() => {
    if (token) {
      setLanguage(getLanguageFromToken(token))
    } else {
      setLanguage("en")
    }
  }, [token])

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

// Default fallback for components rendered outside a LanguageProvider — most useful for unit
// tests that mount a child component in isolation. Production paths always go through
// <LanguageProvider> in App.tsx, so this fallback only applies in test/preview contexts.
const FALLBACK_LANGUAGE_CONTEXT: LanguageContextType = {
  language: "en",
  setLanguage: () => {},
}

export function useLanguage(): LanguageContextType {
  return useContext(LanguageContext) ?? FALLBACK_LANGUAGE_CONTEXT
}
